import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseFieldArray, BaseInput, FormBodyContainer } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { getRewardTypeSelectItems, getRewardWithChanceItem } from '@shared/utils';

import { VipWheelSettingsFormProps } from './types';
import { vipWheelSettingsSchema } from './validationSchema';

import styles from './VipWheelSettingsForm.module.css';

const { inlineInputsContainer } = styles;

export const VipWheelSettingsForm: FC<VipWheelSettingsFormProps> = ({
  initialValues,
  isSubmitting = false,
  submitButtonCaption = 'Update',
  onSubmit,
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={onSubmit}
    validationSchema={vipWheelSettingsSchema}
    enableReinitialize
  >
    {({ submitForm, values, handleChange, handleBlur, errors, setFieldValue, touched }) => {
      const {
        spinResetHours: spinResetHoursValue,
        defaultSpinsNumber: defaultSpinsNumberValue,
        defaultRewardsMap: defaultRewardsMapValue,
        defaultCryptocheckLinks: defaultCryptocheckLinksValue,
        diamondSpinsNumber: diamondSpinsNumberValue,
        diamondRewardsMap: diamondRewardsMapValue,
        diamondCryptocheckLinks: diamondCryptocheckLinksValue,
        eliteSpinsNumber: eliteSpinsNumberValue,
        eliteRewardsMap: eliteRewardsMapValue,
        eliteCryptocheckLinks: eliteCryptocheckLinksValue,
      } = values;
      const {
        spinResetHours: spinResetHoursError,
        defaultSpinsNumber: defaultSpinsNumberError,
        diamondSpinsNumber: diamondSpinsNumberError,
        eliteSpinsNumber: eliteSpinsNumberError,
      } = errors;

      const onVipWheelSettingsSubmit = async () => {
        await submitForm();
      };

      const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

      return (
        <FormBodyContainer
          isSubmitting={!!isSubmitting}
          onSubmit={onVipWheelSettingsSubmit}
          submitButtonCaption={submitButtonCaption}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
        >
          <BaseInput
            type="number"
            helperText={spinResetHoursError}
            id="spinResetHours"
            name="spinResetHours"
            caption="Spin Reset Hours"
            label="Spin Reset Hours"
            value={spinResetHoursValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={!!spinResetHoursError}
          />
          <Box className={inlineInputsContainer}>
            <Typography fontSize="16px" fontWeight={600}>
              Default spins
            </Typography>
            <BaseInput
              type="number"
              helperText={defaultSpinsNumberError}
              id="defaultSpinsNumber"
              name="defaultSpinsNumber"
              caption="Default Spins Count"
              label="Default Spins Count"
              value={defaultSpinsNumberValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!defaultSpinsNumberError}
            />
            <BaseFieldArray
              fieldArrayName="defaultRewardsMap"
              items={defaultRewardsMapValue}
              getBaseFieldValue={getRewardWithChanceItem}
              selectFields={{
                type: getRewardTypeSelectItems(),
              }}
              touched={touched}
              errors={errors}
              label="Reward"
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
            />
            <Box>
              <BaseFieldArray
                fieldArrayName="defaultCryptocheckLinks"
                items={defaultCryptocheckLinksValue ?? []}
                touched={touched}
                errors={errors}
                label="Default Cryptocheck Links"
                handleBlur={handleBlur}
                handleChange={handleChange}
              />
            </Box>
          </Box>
          <Box className={inlineInputsContainer}>
            <Typography fontSize="16px" fontWeight={600}>
              Diamond spins
            </Typography>
            <BaseInput
              type="number"
              helperText={diamondSpinsNumberError}
              id="diamondSpinsNumber"
              name="diamondSpinsNumber"
              caption="Diamond Spins Count"
              label="Diamond Spins Count"
              value={diamondSpinsNumberValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!diamondSpinsNumberError}
            />
            <BaseFieldArray
              fieldArrayName="diamondRewardsMap"
              items={diamondRewardsMapValue}
              getBaseFieldValue={getRewardWithChanceItem}
              selectFields={{
                type: getRewardTypeSelectItems(),
              }}
              touched={touched}
              errors={errors}
              label="Reward"
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
            />
            <Box>
              <BaseFieldArray
                fieldArrayName="diamondCryptocheckLinks"
                items={diamondCryptocheckLinksValue ?? []}
                touched={touched}
                errors={errors}
                label="Diamond Cryptocheck Links"
                handleBlur={handleBlur}
                handleChange={handleChange}
              />
            </Box>
          </Box>
          <Box className={inlineInputsContainer}>
            <Typography fontSize="16px" fontWeight={600}>
              Elite spins
            </Typography>
            <BaseInput
              type="number"
              helperText={eliteSpinsNumberError}
              id="eliteSpinsNumber"
              name="eliteSpinsNumber"
              caption="Elite Spins Count"
              label="Elite Spins Count"
              value={eliteSpinsNumberValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={!!eliteSpinsNumberError}
            />
            <BaseFieldArray
              fieldArrayName="eliteRewardsMap"
              items={eliteRewardsMapValue}
              getBaseFieldValue={getRewardWithChanceItem}
              selectFields={{
                type: getRewardTypeSelectItems(),
              }}
              touched={touched}
              errors={errors}
              label="Reward"
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
            />
            <Box>
              <BaseFieldArray
                fieldArrayName="eliteCryptocheckLinks"
                items={eliteCryptocheckLinksValue ?? []}
                touched={touched}
                errors={errors}
                label="Elite Cryptocheck Links"
                handleBlur={handleBlur}
                handleChange={handleChange}
              />
            </Box>
          </Box>
        </FormBodyContainer>
      );
    }}
  </Formik>
);
